import { ReactNode, useEffect, useEffectEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useMedia } from '../hooks/medias';

import { Media } from '../types/mediaTypes';

export const PROCESSING_PLACEHOLDER =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100%" height="100%" fill="%23e0e0e0"/></svg>';

const POLL_INTERVAL_MS = 2000;

export type ProcessingPhotoItem = {
    id?: number | string;
    url: string;
    processingStatus?: Media['processingStatus'];
    uploading?: boolean;
    uploadingText?: string;
    errorText?: string;
};

type MediaProcessingItemProps<T extends ProcessingPhotoItem> = {
    item: T;
    onProcessingComplete?: (media: Media) => void;
    onProcessingError?: (mediaId: number) => void;
    children: (item: T) => ReactNode;
};

/**
 * Polls an in-progress media until the Batch worker has written its files, and
 * shows a placeholder meanwhile: the CDN url 404s until then.
 */
export function MediaProcessingItem<T extends ProcessingPhotoItem>({
    item,
    onProcessingComplete,
    onProcessingError,
    children,
}: MediaProcessingItemProps<T>) {
    const mediaId = typeof item.id === 'number' ? item.id : undefined;
    // Read the latest poll result before polling again so polling stops once done.
    const latest = useQueryClient().getQueryData<Media>(['media', mediaId]);
    const isDone =
        latest !== undefined && latest.processingStatus !== 'in_progress';
    const { mediasResponse } = useMedia(mediaId, {
        refetchInterval: isDone ? undefined : POLL_INTERVAL_MS,
    });
    const status = mediasResponse?.processingStatus ?? item.processingStatus;

    const notifyFinished = useEffectEvent((media: Media) => {
        if (media.processingStatus === 'completed') {
            onProcessingComplete?.(media);
        } else if (media.processingStatus === 'error') {
            onProcessingError?.(media.id);
        }
    });

    useEffect(() => {
        if (
            mediasResponse &&
            mediasResponse.processingStatus !== 'in_progress'
        ) {
            notifyFinished(mediasResponse);
        }
    }, [mediasResponse]);

    if (status === 'in_progress') {
        return children({
            ...item,
            // A local preview of the picked file is still valid; the CDN url is not.
            url: item.url.startsWith('blob:')
                ? item.url
                : PROCESSING_PLACEHOLDER,
            uploading: true,
            uploadingText: 'Processing',
        });
    }
    if (status === 'error') {
        return children({
            ...item,
            url: PROCESSING_PLACEHOLDER,
            errorText: 'Failed to process',
        });
    }
    return children(item);
}
